import type { Dispatch, SetStateAction } from 'react';
import { format } from 'date-fns';
import { AlertCircle, Calendar, CheckCircle, DollarSign, ExternalLink, GitCommit, GitPullRequest, Search, User } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import type { Commit } from '@/pages/developer/types';
import { extractPullRequestNumberFromCommitMessage, isPullRequestCommit } from '@/pages/developer/githubUtils';

export type CommitHistoryFilter = 'all' | 'pulls';

export type CommitAssignmentFilter = 'all' | 'unassigned' | 'assigned';

export type CommitHistoryCardProps = {
  developerUnlocked: boolean;
  canManageGitOrders: boolean;
  commitBillingSha: string | null;
  billCommit: (commitSha: string) => void;
  unbillCommit: (commitSha: string) => void;

  onAssignCommitToGitOrder?: (commitSha: string) => void;
  onOpenGitOrder?: (gitOrderId: string) => void;
  onAssignFilteredCommitsToGitOrder?: (commitShas: string[]) => void;

  lastCommitSyncAt: string | null;

  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;

  commitFilter: CommitHistoryFilter;
  setCommitFilter: Dispatch<SetStateAction<CommitHistoryFilter>>;

  commitAssignmentFilter: CommitAssignmentFilter;
  setCommitAssignmentFilter: Dispatch<SetStateAction<CommitAssignmentFilter>>;

  commitsPerPage: 10 | 25 | 50 | 100;
  setCommitsPerPage: Dispatch<SetStateAction<10 | 25 | 50 | 100>>;

  clampedCommitPage: number;
  totalCommitPages: number;
  setCommitPage: Dispatch<SetStateAction<number>>;
  handleNextCommitPage: () => void;

  displayedTotals: { additions: number; deletions: number };
  firestoreTotalAdditions: number | null;
  firestoreTotalDeletions: number | null;
  dataSource: 'firestore' | 'local' | 'github';
  firestoreCommitCount: number | null;
  commitsTotal: number;
  firestoreCommitsLoadedCount: number;
  firestoreCommitsPagingLoading: boolean;
  canLoadMoreFirestoreCommits: boolean;
  firestoreCommitsPagingError: string | null;

  pagedCommits: Commit[];
  filteredCommitsLength: number;
  filteredCommitShas: string[];

  ratePerCommit: number;
};

export function CommitHistoryCard({
  developerUnlocked,
  canManageGitOrders,
  commitBillingSha,
  billCommit,
  unbillCommit,
  onAssignCommitToGitOrder,
  onOpenGitOrder,
  onAssignFilteredCommitsToGitOrder,
  lastCommitSyncAt,
  searchQuery,
  setSearchQuery,
  commitFilter,
  setCommitFilter,
  commitAssignmentFilter,
  setCommitAssignmentFilter,
  commitsPerPage,
  setCommitsPerPage,
  clampedCommitPage,
  totalCommitPages,
  setCommitPage,
  handleNextCommitPage,
  displayedTotals,
  firestoreTotalAdditions,
  firestoreTotalDeletions,
  dataSource,
  firestoreCommitCount,
  commitsTotal,
  firestoreCommitsLoadedCount,
  firestoreCommitsPagingLoading,
  canLoadMoreFirestoreCommits,
  firestoreCommitsPagingError,
  pagedCommits,
  filteredCommitsLength,
  filteredCommitShas,
  ratePerCommit,
}: CommitHistoryCardProps) {
  const isFilteredView = searchQuery.trim().length > 0 || commitFilter !== 'all' || commitAssignmentFilter !== 'all';
  const showAssignToGitOrder = canManageGitOrders && isFilteredView && typeof onAssignCommitToGitOrder === 'function';
  const showBulkAssign =
    canManageGitOrders &&
    isFilteredView &&
    filteredCommitShas.length > 0 &&
    typeof onAssignFilteredCommitsToGitOrder === 'function';

  const handleOpenGitOrder = (commit: Commit) => {
    const gitOrderId = (commit.workorderId ?? '').trim();
    if (!gitOrderId) return;
    if (typeof onOpenGitOrder === 'function') {
      onOpenGitOrder(gitOrderId);
      return;
    }
    if (typeof onAssignCommitToGitOrder === 'function') {
      onAssignCommitToGitOrder(commit.sha);
    }
  };

  return (
    <Card id="commit-history" className="mb-8">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <GitCommit className="w-5 h-5" />
              Commit History & Billing
            </CardTitle>
            <CardDescription>Load the full GitHub history once, then manually add new commits as they arrive.</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs text-muted-foreground">Sync actions live in the Developer Dialog.</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {lastCommitSyncAt && (
          <p className="text-xs text-muted-foreground mb-4">
            Last manual sync: {format(new Date(lastCommitSyncAt), 'MMM d, yyyy · h:mm a')}
          </p>
        )}

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search commits by message, SHA, or author..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>Per page</span>
            <select
              className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={commitsPerPage}
              onChange={(event) => setCommitsPerPage(Number(event.target.value) as 10 | 25 | 50 | 100)}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>
              Page {clampedCommitPage} of {totalCommitPages}
            </span>
            <span className="text-xs">
              · Total +{displayedTotals.additions.toLocaleString()} / -{displayedTotals.deletions.toLocaleString()}
              {typeof firestoreTotalAdditions !== 'number' &&
              typeof firestoreTotalDeletions !== 'number' &&
              dataSource === 'firestore' &&
              typeof firestoreCommitCount === 'number' &&
              firestoreCommitCount > commitsTotal
                ? ' (loaded subset)'
                : ''}
            </span>
            {dataSource === 'firestore' && typeof firestoreCommitCount === 'number' && (
              <span className="text-xs">
                · Loaded {Math.min(firestoreCommitsLoadedCount, firestoreCommitCount)} of {firestoreCommitCount}
                {firestoreCommitsPagingLoading ? ' (loading...)' : ''}
              </span>
            )}

            <span className="hidden sm:inline">·</span>
            <span className="text-sm">Filter</span>
            <select
              className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={commitFilter}
              onChange={(event) => setCommitFilter(event.target.value as CommitHistoryFilter)}
            >
              <option value="all">All</option>
              <option value="pulls">Pull requests</option>
            </select>

            <span className="hidden sm:inline">·</span>
            <span className="text-sm">Assignment</span>
            <select
              className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={commitAssignmentFilter}
              onChange={(event) => setCommitAssignmentFilter(event.target.value as CommitAssignmentFilter)}
            >
              <option value="unassigned">Unassigned</option>
              <option value="all">All</option>
              <option value="assigned">Assigned</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            {developerUnlocked && showBulkAssign && (
              <Button type="button" variant="outline" onClick={() => onAssignFilteredCommitsToGitOrder(filteredCommitShas)}>
                Assign filtered to Git order ({filteredCommitShas.length})
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              disabled={clampedCommitPage <= 1}
              onClick={() => setCommitPage((prev) => Math.max(1, prev - 1))}
            >
              Prev
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={(clampedCommitPage >= totalCommitPages && !canLoadMoreFirestoreCommits) || firestoreCommitsPagingLoading}
              onClick={handleNextCommitPage}
            >
              Next
            </Button>
          </div>
        </div>

        {firestoreCommitsPagingError && (
          <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
            {firestoreCommitsPagingError}
          </div>
        )}

        {commitsTotal === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <GitCommit className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No commits loaded</p>
            <p className="text-sm">Use the Developer Dialog to import commits from GitHub.</p>
          </div>
        ) : (
          <>
            <div className="sm:hidden space-y-2">
              {pagedCommits.map((commit) => {
                const pullRequestCommit = isPullRequestCommit(commit);
                const amount = !pullRequestCommit && commit.billed ? (commit.billingAmount ?? ratePerCommit) : 0;
                const prNumber = extractPullRequestNumberFromCommitMessage(commit.message);

                return (
                  <div key={commit.sha} className="rounded-lg border border-border/60 bg-muted/10 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="font-mono text-xs">
                            {developerUnlocked && commit.url ? (
                              <a
                                href={commit.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline inline-flex items-center gap-1"
                              >
                                {commit.sha.slice(0, 7)}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : (
                              <span className="text-muted-foreground">{commit.sha.slice(0, 7)}</span>
                            )}
                          </span>

                          <span className="text-xs text-muted-foreground">
                            {format(new Date(commit.date), 'MMM d, yyyy')}
                          </span>

                          {commit.milestoneTitle && (
                            <Badge variant="outline" className="text-xs" title={commit.milestoneTitle}>
                              Milestone
                            </Badge>
                          )}

                          {isPullRequestCommit(commit) && (
                            <Badge
                              variant="outline"
                              className="text-xs cursor-pointer"
                              title={prNumber ? `Pull request #${prNumber}` : 'Pull request'}
                            >
                              <span className="inline-flex items-center gap-1">
                                <GitPullRequest className="w-3 h-3" />
                                PR
                              </span>
                            </Badge>
                          )}

                          {developerUnlocked && (commit.workorderId ?? '').trim() && (
                            <Badge
                              variant="outline"
                              className="text-xs cursor-pointer"
                              title={`Git order ${commit.workorderId}`}
                              onClick={() => handleOpenGitOrder(commit)}
                            >
                              GO {commit.workorderId?.slice(0, 7)}
                            </Badge>
                          )}
                        </div>

                        <p className="mt-2 text-sm font-medium leading-snug break-words">{commit.message}</p>

                        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                          <User className="w-3 h-3" />
                          <span className="truncate">{commit.author}</span>
                        </div>
                      </div>

                      <div className="shrink-0 text-right">
                        <div className="flex items-center justify-end gap-1 text-sm font-medium">
                          <DollarSign className="w-3 h-3 text-muted-foreground" />
                          {amount}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-col gap-2">
                      {pullRequestCommit ? (
                        commit.billed && developerUnlocked ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full gap-2"
                            disabled={commitBillingSha === commit.sha}
                            title="Pull request commits are not billable. Tap to unbill."
                            onClick={() => unbillCommit(commit.sha)}
                          >
                            <GitPullRequest className="w-4 h-4" />
                            Pull Request (tap to unbill)
                          </Button>
                        ) : (
                          <div className="w-full">
                            <Badge variant="outline" className="w-full justify-center gap-2" title="Pull request commits are not billable.">
                              <GitPullRequest className="w-4 h-4" />
                              Pull Request
                            </Badge>
                          </div>
                        )
                      ) : commit.billed ? (
                        developerUnlocked ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full gap-2"
                            disabled={commitBillingSha === commit.sha}
                            onClick={() => unbillCommit(commit.sha)}
                          >
                            <CheckCircle className="w-4 h-4" />
                            Billed (tap to unbill)
                          </Button>
                        ) : (
                          <div className="w-full">
                            <Badge
                              variant="default"
                              className="w-full justify-center gap-2 bg-green-500/20 text-green-500 border-green-500/30"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Billed
                            </Badge>
                          </div>
                        )
                      ) : developerUnlocked ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full gap-2"
                          disabled={commitBillingSha === commit.sha}
                          onClick={() => billCommit(commit.sha)}
                        >
                          <AlertCircle className="w-4 h-4" />
                          Unbilled (tap to bill)
                        </Button>
                      ) : (
                        <div className="w-full">
                          <Badge variant="outline" className="w-full justify-center gap-2 text-amber-500 border-amber-500/30">
                            <AlertCircle className="w-4 h-4" />
                            Unbilled
                          </Badge>
                        </div>
                      )}

                      {developerUnlocked && showAssignToGitOrder && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            if ((commit.workorderId ?? '').trim()) {
                              handleOpenGitOrder(commit);
                              return;
                            }
                            onAssignCommitToGitOrder(commit.sha);
                          }}
                        >
                          {commit.workorderId ? 'Open Git order' : 'Assign to Git order'}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="hidden sm:block rounded-lg border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <Table className="min-w-[980px]">
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="w-[100px]">SHA</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Author</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Changes</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      {showAssignToGitOrder && <TableHead className="text-right">Git order</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedCommits.map((commit) => {
                      const pullRequestCommit = isPullRequestCommit(commit);
                      const amount = !pullRequestCommit && commit.billed ? (commit.billingAmount ?? ratePerCommit) : 0;
                      return (
                      <TableRow key={commit.sha}>
                        <TableCell className="font-mono text-xs">
                          {developerUnlocked && commit.url ? (
                            <a
                              href={commit.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline flex items-center gap-1"
                            >
                              {commit.sha.slice(0, 7)}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span className="text-muted-foreground">{commit.sha.slice(0, 7)}</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[300px] truncate" title={commit.message}>
                          <div className="flex items-center gap-2">
                            <span className="truncate">{commit.message}</span>
                            {commit.milestoneTitle && (
                              <Badge variant="outline" className="shrink-0 text-xs" title={commit.milestoneTitle}>
                                Milestone
                              </Badge>
                            )}

                            {isPullRequestCommit(commit) && (
                              <Badge
                                variant="outline"
                                className="shrink-0 text-xs"
                                title={(() => {
                                  const prNumber = extractPullRequestNumberFromCommitMessage(commit.message);
                                  return prNumber ? `Pull request #${prNumber}` : 'Pull request';
                                })()}
                              >
                                <span className="inline-flex items-center gap-1">
                                  <GitPullRequest className="w-3 h-3" />
                                  PR
                                </span>
                              </Badge>
                            )}

                            {developerUnlocked && (commit.workorderId ?? '').trim() && (
                              <Badge
                                variant="outline"
                                className="shrink-0 text-xs cursor-pointer"
                                title={`Git order ${commit.workorderId}`}
                                onClick={() => handleOpenGitOrder(commit)}
                              >
                                GO {commit.workorderId?.slice(0, 7)}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="flex items-center gap-2">
                          <User className="w-3 h-3 text-muted-foreground" />
                          {commit.author}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(commit.date), 'MMM d, yyyy')}
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-xs font-mono">
                          {(() => {
                            const additions = typeof commit.additions === 'number' ? commit.additions : null;
                            const deletions = typeof commit.deletions === 'number' ? commit.deletions : null;

                            const looksUnknown =
                              (additions === null && deletions === null) ||
                              (additions === 0 && deletions === 0 && typeof commit.filesChanged !== 'number');

                            if (looksUnknown) {
                              return <span className="text-muted-foreground">—</span>;
                            }

                            return (
                              <>
                                <span className="text-green-500">+{additions ?? '—'}</span>
                                {' / '}
                                <span className="text-red-500">-{deletions ?? '—'}</span>
                              </>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          <div className="flex items-center justify-end gap-1">
                            <DollarSign className="w-3 h-3 text-muted-foreground" />
                            {amount}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {pullRequestCommit ? (
                            commit.billed && developerUnlocked ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="gap-1"
                                disabled={commitBillingSha === commit.sha}
                                title="Pull request commits are not billable. Click to unbill."
                                onClick={() => unbillCommit(commit.sha)}
                              >
                                <GitPullRequest className="w-3 h-3" />
                                Pull Request
                              </Button>
                            ) : (
                              <Badge variant="outline" className="gap-1" title="Pull request commits are not billable.">
                                <GitPullRequest className="w-3 h-3" />
                                Pull Request
                              </Badge>
                            )
                          ) : commit.billed ? (
                            developerUnlocked ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="gap-1"
                                disabled={commitBillingSha === commit.sha}
                                onClick={() => unbillCommit(commit.sha)}
                              >
                                <CheckCircle className="w-3 h-3" />
                                Billed
                              </Button>
                            ) : (
                              <Badge variant="default" className="gap-1 bg-green-500/20 text-green-500 border-green-500/30">
                                <CheckCircle className="w-3 h-3" />
                                Billed
                              </Badge>
                            )
                          ) : developerUnlocked ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="gap-1"
                              disabled={commitBillingSha === commit.sha}
                              onClick={() => billCommit(commit.sha)}
                            >
                              <AlertCircle className="w-3 h-3" />
                              Unbilled ($0)
                            </Button>
                          ) : (
                            <Badge variant="outline" className="gap-1 text-amber-500 border-amber-500/30">
                              <AlertCircle className="w-3 h-3" />
                              Unbilled ($0)
                            </Badge>
                          )}
                        </TableCell>

                        {showAssignToGitOrder && (
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if ((commit.workorderId ?? '').trim()) {
                                  handleOpenGitOrder(commit);
                                  return;
                                }
                                onAssignCommitToGitOrder(commit.sha);
                              }}
                            >
                              {commit.workorderId ? 'Git order' : 'Assign'}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        )}

        {filteredCommitsLength > 0 && (
          <p className="text-sm text-muted-foreground mt-3">
            Showing {(clampedCommitPage - 1) * commitsPerPage + 1}-
            {Math.min(clampedCommitPage * commitsPerPage, filteredCommitsLength)} of {filteredCommitsLength}
            {filteredCommitsLength !== commitsTotal ? ` filtered (${commitsTotal} total)` : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
